<script lang="ts">
	/**
	 * On-device overlay to diagnose safe-area / double-inset issues.
	 * Enable from Dev Tools modal. Compare:
	 *  - env(safe-area-inset-*)  → what CSS thinks the OS inset is
	 *  - .main-layout padding-top → what we actually apply
	 *  - visual top band (red)   → should match env top if CSS is the only inset
	 *  - visual bottom band (blue) → should match env bottom (home indicator / nav)
	 *
	 * If the red band is ~safe-area but content sits much lower → native WebView
	 * is also insetting (contentInsetAdjustmentBehavior / similar).
	 * If env top is 0 but content is still low → native-only inset (CSS not involved).
	 */
	import { onMount } from 'svelte';
	import { app } from 'src/stores/session/native-bridge';
	import { safeAreaDebugEnabled } from 'src/stores/features/dev/safe-area-debug';

	type Insets = { top: number; right: number; bottom: number; left: number };

	let insets: Insets = { top: 0, right: 0, bottom: 0, left: 0 };
	let layoutPaddingTop = 0;
	let layoutHeight = 0;
	let innerH = 0;
	let clientH = 0;
	let vvH = 0;
	let displayMode = 'unknown';
	let iosStandalone = false;
	let collapsed = false;
	/** Hides the metrics panel until the feature is turned off and on again. */
	let panelDismissed = false;

	const parsePx = (value: string) => {
		const n = parseFloat(value);
		return Number.isFinite(n) ? n : 0;
	};

	const readEnvInset = (side: keyof Insets) => {
		const probe = document.createElement('div');
		probe.style.cssText = `
			position: absolute; visibility: hidden; pointer-events: none;
			padding-${side}: env(safe-area-inset-${side}, 0px);
		`;
		document.body.appendChild(probe);
		const px = parsePx(getComputedStyle(probe).getPropertyValue(`padding-${side}`));
		probe.remove();
		return px;
	};

	const refresh = () => {
		if (!$safeAreaDebugEnabled) return;

		insets = {
			top: readEnvInset('top'),
			right: readEnvInset('right'),
			bottom: readEnvInset('bottom'),
			left: readEnvInset('left'),
		};

		const layout = document.querySelector('.main-layout') as HTMLElement | null;
		if (layout) {
			const cs = getComputedStyle(layout);
			layoutPaddingTop = parsePx(cs.paddingTop);
			layoutHeight = Math.round(layout.getBoundingClientRect().height);
		} else {
			layoutPaddingTop = 0;
			layoutHeight = 0;
		}

		innerH = Math.round(window.innerHeight);
		clientH = Math.round(document.documentElement.clientHeight);
		vvH = Math.round(window.visualViewport?.height ?? 0);

		const modes = ['fullscreen', 'standalone', 'minimal-ui', 'browser'] as const;
		displayMode = modes.find((m) => window.matchMedia(`(display-mode: ${m})`).matches) ?? 'unknown';
		iosStandalone = Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
	};

	const dismissPanel = () => {
		panelDismissed = true;
		collapsed = false;
	};

	$: verdict = (() => {
		if (!$safeAreaDebugEnabled) return '';
		const ratio = insets.top > 0 ? layoutPaddingTop / insets.top : 0;
		if (insets.top === 0 && layoutPaddingTop === 0) {
			return $app
				? 'env=0 + pad=0 — if gap remains, native shell is insetting the WebView'
				: 'env=0 + pad=0 — no CSS safe-area; gap is not from env()';
		}
		if (insets.top > 0 && Math.abs(layoutPaddingTop - insets.top) <= 1) {
			return 'CSS pad ≈ env(top) — single CSS inset OK; large band is the real OS inset (or native also pads visually)';
		}
		if (insets.top > 0 && ratio >= 1.8) {
			return 'pad ≈ 2× env(top) — double inset in CSS cascade; check stacked padding rules';
		}
		if (insets.top > 0 && layoutPaddingTop > insets.top + 8) {
			return 'pad > env(top) — extra CSS padding on top of safe-area';
		}
		return 'Compare red (top) / blue (bottom) band heights to content offset manually';
	})();

	// Re-show the metrics panel whenever the feature is turned back on.
	$: if (!$safeAreaDebugEnabled) {
		panelDismissed = false;
		collapsed = false;
	}

	$: if ($safeAreaDebugEnabled) refresh();

	onMount(() => {
		const onResize = () => refresh();
		window.visualViewport?.addEventListener('resize', onResize);
		window.addEventListener('resize', onResize);
		const timer = setInterval(() => {
			if ($safeAreaDebugEnabled) refresh();
		}, 500);
		return () => {
			clearInterval(timer);
			window.visualViewport?.removeEventListener('resize', onResize);
			window.removeEventListener('resize', onResize);
		};
	});
</script>

{#if $safeAreaDebugEnabled}
	<!-- Visual ruler: exact env(safe-area-inset-top) height -->
	<div
		class="safe-area-ruler safe-area-ruler-top"
		style={`height: ${insets.top}px;`}
		aria-hidden="true"
		title="env(safe-area-inset-top)"
	>
		<span>env top {insets.top}px</span>
	</div>

	<!-- Visual ruler: exact env(safe-area-inset-bottom) height -->
	<div
		class="safe-area-ruler safe-area-ruler-bottom"
		style={`height: ${insets.bottom}px;`}
		aria-hidden="true"
		title="env(safe-area-inset-bottom)"
	>
		<span>env bottom {insets.bottom}px</span>
	</div>

	{#if !panelDismissed}
		<div class="safe-area-panel" class:collapsed>
			<div class="panel-header">
				<button type="button" class="toggle" on:click={() => (collapsed = !collapsed)}>
					{collapsed ? 'SafeArea' : 'SafeArea debug ▾'}
				</button>
				<button
					type="button"
					class="dismiss"
					aria-label="Dismiss safe area debug panel"
					on:click={dismissPanel}
				>
					×
				</button>
			</div>
			{#if !collapsed}
				<pre>
env top/right/bottom/left: {insets.top} / {insets.right} / {insets.bottom} / {insets.left}
.main-layout padding-top:  {layoutPaddingTop}px
.main-layout height:       {layoutHeight}px
inner / client / vv:       {innerH} / {clientH} / {vvH}
display-mode:              {displayMode}
iOS navigator.standalone:  {iosStandalone}
native $app:               {$app}
viewport-fit:              check index.html (cover)
— —
{verdict}
				</pre>
			{/if}
		</div>
	{/if}
{/if}

<style>
	.safe-area-ruler {
		position: fixed;
		left: 0;
		right: 0;
		z-index: 99998;
		pointer-events: none;
		display: flex;
		justify-content: center;
		box-sizing: border-box;
		font: 10px/1.2 ui-monospace, monospace;
		color: #fff;
		text-shadow: 0 1px 2px #000;
	}

	.safe-area-ruler-top {
		top: 0;
		align-items: flex-end;
		padding-bottom: 2px;
		background: rgba(255, 0, 0, 0.35);
		border-bottom: 1px solid rgba(255, 80, 80, 0.9);
	}

	.safe-area-ruler-bottom {
		bottom: 0;
		align-items: flex-start;
		padding-top: 2px;
		background: rgba(0, 120, 255, 0.35);
		border-top: 1px solid rgba(80, 160, 255, 0.9);
	}

	.safe-area-panel {
		position: fixed;
		right: 8px;
		bottom: calc(96px + env(safe-area-inset-bottom, 0px));
		z-index: 99999;
		max-width: min(340px, calc(100vw - 16px));
		background: rgba(0, 0, 0, 0.82);
		color: #b8f5c2;
		border: 1px solid rgba(255, 255, 255, 0.2);
		border-radius: 10px;
		padding: 6px 8px;
		font:
			11px/1.35 ui-monospace,
			Menlo,
			monospace;
		backdrop-filter: blur(8px);
	}

	.safe-area-panel.collapsed {
		padding: 4px 8px;
	}

	.panel-header {
		display: flex;
		align-items: center;
		gap: 4px;
		margin-bottom: 4px;
	}

	.safe-area-panel.collapsed .panel-header {
		margin-bottom: 0;
	}

	.toggle {
		display: block;
		flex: 1;
		min-width: 0;
		margin: 0;
		padding: 2px 0;
		border: 0;
		background: transparent;
		color: #fff;
		font: inherit;
		font-weight: 600;
		text-align: left;
		cursor: pointer;
	}

	.dismiss {
		flex-shrink: 0;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 22px;
		height: 22px;
		margin: 0;
		padding: 0;
		border: 0;
		border-radius: 6px;
		background: rgba(255, 255, 255, 0.12);
		color: #fff;
		font: 16px/1 ui-sans-serif, system-ui, sans-serif;
		cursor: pointer;
	}

	.dismiss:hover,
	.dismiss:focus-visible {
		background: rgba(255, 255, 255, 0.22);
		outline: none;
	}

	pre {
		margin: 0;
		white-space: pre-wrap;
		word-break: break-word;
	}
</style>
